import { useState } from 'react';

const regions = [
  {
    state: 'Texas',
    abbr: 'TX',
    cities: ['Houston', 'Katy', 'Cypress', 'Sugar Land', 'Dickinson'],
    highlight: 'Roofing services in Houston, TX',
    detail: 'Our Texas headquarters serves the greater Houston metro and surrounding communities with full roofing, storm restoration, and exterior services.',
    img: 'https://public.readdy.ai/ai/img_res/45ff2ae9a0bec035e5166e16eb393f46.jpg',
  },
  {
    state: 'Louisiana',
    abbr: 'LA',
    cities: ['Baton Rouge', 'Lafayette', 'Broussard'],
    highlight: 'Storm damage roofing in Lafayette, LA',
    detail: 'Serving Louisiana communities with specialized hurricane and storm restoration expertise, insurance coordination, and premium roofing systems.',
    img: 'https://readdy.ai/api/search-image?query=beautiful%20aerial%20view%20of%20Baton%20Rouge%20Louisiana%20upscale%20residential%20neighborhood%2C%20elegant%20southern%20style%20homes%20with%20new%20rooftops%2C%20lush%20mature%20oak%20trees%2C%20warm%20golden%20sunlight%2C%20vibrant%20green%20lawns%2C%20clear%20sky%2C%20cinematic%20high%20resolution%20photography&width=1200&height=800&seq=area_premium_02&orientation=landscape',
  },
  {
    state: 'North Carolina',
    abbr: 'NC',
    cities: ['Charlotte'],
    highlight: 'Roof replacement in Charlotte, NC',
    detail: 'Bringing Southeast roofing expertise to the Charlotte market with premium installations and comprehensive storm restoration services.',
    img: 'https://readdy.ai/api/search-image?query=stunning%20aerial%20view%20of%20Charlotte%20North%20Carolina%20affluent%20suburban%20neighborhood%2C%20luxury%20homes%20with%20beautiful%20rooftops%2C%20tree-lined%20streets%2C%20bright%20sunny%20day%2C%20vibrant%20green%20landscape%2C%20clear%20blue%20sky%2C%20ultra%20high%20resolution%20cinematic%20photography&width=1200&height=800&seq=area_premium_03&orientation=landscape',
  },
  {
    state: 'Georgia',
    abbr: 'GA',
    cities: ['Atlanta'],
    highlight: 'Commercial roofing in Atlanta, GA',
    detail: 'Serving Atlanta\'s residential and commercial markets with expert roofing installations, repairs, and storm damage restoration.',
    img: 'https://readdy.ai/api/search-image?query=breathtaking%20aerial%20drone%20view%20of%20Atlanta%20Georgia%20upscale%20residential%20neighborhood%2C%20luxury%20homes%20with%20pristine%20rooftops%2C%20lush%20green%20trees%2C%20bright%20golden%20sunlight%2C%20wide%20streets%2C%20vibrant%20colors%2C%20ultra%20high%20resolution%20cinematic%20photography&width=1200&height=800&seq=area_premium_04&orientation=landscape',
  },
];

export default function ServiceAreas() {
  const [active, setActive] = useState(0);

  return (
    <section id="areas" className="py-28 bg-[#111111]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <span className="text-[#c0392b] text-xs font-bold tracking-widest uppercase mb-3 block">Where We Work</span>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
              Serving Major Markets<br />Across the Southeast
            </h2>
          </div>
          <p className="text-white/40 text-sm max-w-sm leading-relaxed">
            With multiple offices and regional teams, PRA delivers expert roofing and storm restoration services tailored to local conditions.
          </p>
        </div>

        {/* Main interactive layout */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left — tab selectors */}
          <div className="lg:w-[280px] flex flex-row lg:flex-col gap-2">
            {regions.map((region, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`flex-1 lg:flex-none text-left px-5 py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  active === i
                    ? 'bg-[#c0392b] text-white'
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-black tracking-widest ${active === i ? 'text-white/70' : 'text-white/30'}`}>
                    {region.abbr}
                  </span>
                  <span className="font-bold text-sm hidden sm:block">{region.state}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Right — active region display */}
          <div className="flex-1 relative rounded-2xl overflow-hidden min-h-[420px] lg:min-h-[500px]">
            {regions.map((region, i) => (
              <div
                key={i}
                className={`absolute inset-0 transition-opacity duration-500 ${active === i ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              >
                <img
                  src={region.img}
                  alt={`Roofing services in ${region.state}`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/95 via-[#0d0d0d]/30 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/60 to-transparent"></div>

                {/* Content overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-[#c0392b] text-white text-xs font-black px-3 py-1.5 rounded-full tracking-widest uppercase">
                      {region.abbr}
                    </span>
                    <h3 className="text-white font-black text-3xl md:text-4xl">{region.state}</h3>
                  </div>

                  <p className="text-white/65 text-sm leading-relaxed max-w-lg mb-6">{region.detail}</p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {region.cities.map((city, j) => (
                      <span
                        key={j}
                        className="bg-white/10 backdrop-blur-sm text-white text-xs px-4 py-1.5 rounded-full border border-white/20 font-medium"
                      >
                        {city}
                      </span>
                    ))}
                  </div>

                  <p className="text-[#c0392b] text-xs font-bold tracking-wide flex items-center gap-2">
                    <i className="ri-map-pin-line"></i>
                    {region.highlight}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-white/25 text-xs mt-8">
          Extended coverage includes surrounding areas. Contact us to confirm service availability in your location.
        </p>
      </div>
    </section>
  );
}
